import { BehaviorSubject, Observable, defer, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

/**
 * A front-end edit session: every change a page makes is held here and NOTHING
 * is written until Save.
 *
 * The workspace pages (Territories, Sales Route, and the Copilot that drives
 * them) all owe the same four promises, so they are implemented once:
 *
 *   Save   — atomic. One call to the page's `commit`; on failure nothing is
 *            partially saved AND the user's edits are kept so they can retry.
 *   Cancel — back to the last successfully saved state.
 *   Undo   — steps back through unsaved operations one at a time; the stack is
 *            cleared by a successful save.
 *   Shared — the table, the map and the Copilot read ONE `state$` and push into
 *            ONE stack, so a change made on either surface is instantly the
 *            other's too, and there is one Save/Cancel for the page header.
 *
 * ## Why a fold over an operation list rather than a state + inverse ops
 *
 * The session keeps the last saved state plus an ordered list of unsaved
 * operations, and derives the current state by folding them. That costs a
 * re-fold per undo (an edit session is tens of operations, not thousands) and
 * buys three things that inverse-operations do not give for free:
 *
 *  - **Selective undo.** The design requires a Copilot change to be undoable on
 *    its own, not just as the top of the stack. Dropping element *i* and
 *    re-folding is exact; composing inverses out of order is not.
 *  - **No inverse to write.** A page supplies one pure reducer. Getting an
 *    inverse right for "polygon edited, 14 stops reassigned" is the kind of
 *    thing that is subtly wrong for a year.
 *  - **A literal change list.** The yellow "This edit session" strip is the
 *    operation list — it cannot drift from what will be saved, because it *is*
 *    what will be saved.
 *
 * ## Why plain-data operations
 *
 * `TOp` is the page's own type (usually a discriminated union) and carries no
 * behaviour: the page passes `reduce(state, op)`. Plain data means an operation
 * can be logged, compared, sent to the server as the change list, or proposed by
 * the Copilot without the Copilot having to construct closures over page state.
 *
 * ## Not a new state paradigm
 *
 * State is exposed as `BehaviorSubject`-backed observables, matching the rest
 * of the app (WebApp/README and design.md § Patterns: "Observable services
 * (RxJS)"). No signals, no store.
 *
 * ## Usage
 *
 *     type StopOp =
 *       | { kind: 'move'; label: string; stopId: number; toRepId: number }
 *       | { kind: 'reorder'; label: string; order: number[] };
 *
 *     this.session = new EditSession<RouteState, StopOp>({
 *       initial: loaded,
 *       reduce: (s, op) => op.kind === 'move' ? moveStop(s, op) : reorder(s, op),
 *       commit: (state, ops) => this.api.saveRoute(state, ops),
 *       registry: inject(EditSessionRegistry),
 *     });
 */

/** Who proposed a change. Drives the visual convention: solid = saved, dashed
 *  amber = proposed by the user this session, purple = proposed by the Copilot. */
export type EditOrigin = 'user' | 'copilot';

/** The minimum every page operation must carry, so the shared strip can list it
 *  and colour it without knowing the page's domain. */
export interface EditSessionOp {
  /** One line for the "This edit session" strip, e.g. "Moved 3 stops to Ana R." */
  readonly label: string;
  /** Defaults to 'user'. */
  readonly origin?: EditOrigin;
}

/** What `commit` gets and what the session records on success. */
export type EditSessionCommit<TState, TOp extends EditSessionOp> =
  (state: TState, ops: readonly TOp[]) => Observable<TState | void>;

export interface EditSessionConfig<TState, TOp extends EditSessionOp> {
  /** The last saved state — normally what the page just loaded from the server. */
  readonly initial: TState;
  /**
   * Pure reducer: return a NEW state, never mutate `state`. It is called again
   * for every surviving operation after an undo, so a reducer with side effects
   * would fire them repeatedly.
   */
  readonly reduce: (state: TState, op: TOp) => TState;
  /**
   * Persist the whole session in one server round trip. Whatever atomicity the
   * page needs is the backend's: the session's own guarantee is only that it
   * neither clears the operation stack nor moves the saved baseline unless this
   * observable completed successfully.
   *
   * May return the saved state (preferred — the server's version becomes the new
   * baseline) or nothing (the locally folded state becomes the baseline).
   */
  readonly commit?: EditSessionCommit<TState, TOp>;
  /** Register with the app-wide registry so the route guard and the browser's
   *  own unload prompt can see unsaved work. */
  readonly registry?: EditSessionRegistrar;
}

/** The slice of `EditSessionRegistry` a session needs — an interface so
 *  `edit-session.ts` stays free of Angular DI and is unit-testable on its own. */
export interface EditSessionRegistrar {
  register(session: DirtyCheckable): () => void;
}

/** Anything that can say whether it holds unsaved work. */
export interface DirtyCheckable {
  readonly dirty: boolean;
}

/**
 * What a shared control needs from a session, with the page's own types erased.
 *
 * `EditSession<TState, TOp>` is invariant in `TOp` (its reducer takes one), so
 * `EditSession<any, EditSessionOp>` does NOT accept a page's concrete session —
 * a shared bar has to ask for the read-and-command surface instead, which is
 * covariant and which every session satisfies structurally.
 */
export interface EditSessionView {
  readonly changes$: Observable<readonly EditSessionChange<EditSessionOp>[]>;
  readonly saving$: Observable<boolean>;
  readonly saveError$: Observable<unknown>;
  readonly dirty: boolean;
  readonly saving: boolean;
  save(): Observable<unknown>;
  cancel(): void;
  undo(): void;
  undoAt(index: number): void;
}

/**
 * How a surface that is NOT the routed page reaches the page's session.
 *
 * The agent composer is mounted at app level, beside the router outlet rather
 * than inside it. It has no reference to the component holding the session,
 * and it must not open a second one.
 *
 * So the page publishes this on `EditSessionRegistry` — the object that already
 * exists to let code outside a page ask about that page's session, for the
 * unsaved-changes guard. One directory, not two: a second channel would be a
 * second answer to "which session is the live one".
 *
 * ## Why `propose` takes the erased op and returns a boolean
 *
 * `EditSession<TState, TOp>` is invariant in `TOp` — the same reason
 * `EditSessionView` exists — and an operation is the page's own discriminated
 * union, which nothing outside the page can construct correctly. The page
 * therefore supplies a closure that narrows the erased operation to its own type
 * and applies it with `origin: 'copilot'`; the boolean is the page saying
 * whether it recognised the proposal at all, so a caller is never left believing
 * an edit landed when it did not.
 */
export interface CopilotEditTarget {
  /** The page's session, through the shared read-and-command surface — the same
   *  one the header's Save / Cancel / Undo bar is handed. */
  readonly session: EditSessionView;
  /** Apply one Copilot-originated operation, or return false if this page has
   *  no such operation. */
  propose(op: EditSessionOp): boolean;
}

/** One entry of the "This edit session" strip. */
export interface EditSessionChange<TOp extends EditSessionOp> {
  readonly index: number;
  readonly op: TOp;
  readonly label: string;
  readonly origin: EditOrigin;
}

export class EditSession<TState, TOp extends EditSessionOp> implements DirtyCheckable {
  private readonly reduce: (state: TState, op: TOp) => TState;
  private readonly commitFn?: EditSessionCommit<TState, TOp>;
  private readonly unregister: () => void;

  /** The last state the server accepted. Only a successful save moves it. */
  private saved: TState;
  /** Unsaved operations, oldest first. */
  private stack: TOp[] = [];

  private readonly stateSubject: BehaviorSubject<TState>;
  private readonly changesSubject = new BehaviorSubject<readonly EditSessionChange<TOp>[]>([]);
  private readonly savingSubject = new BehaviorSubject<boolean>(false);
  private readonly saveErrorSubject = new BehaviorSubject<unknown>(null);

  /** The state every surface renders: saved state with the unsaved stack folded
   *  in. One stream, so the table, the map and the Copilot cannot disagree. */
  readonly state$: Observable<TState>;
  /** The "This edit session" strip's contents, in the order they were made. */
  readonly changes$: Observable<readonly EditSessionChange<TOp>[]>;
  readonly saving$: Observable<boolean>;
  /** The last save failure, or null. Cleared when a save succeeds or a new one
   *  starts — a stale error next to live edits reads as "still broken". */
  readonly saveError$: Observable<unknown>;
  /** True while there is anything to save. */
  readonly dirty$: Observable<boolean>;

  constructor(config: EditSessionConfig<TState, TOp>) {
    this.saved = config.initial;
    this.reduce = config.reduce;
    this.commitFn = config.commit;

    this.stateSubject = new BehaviorSubject<TState>(config.initial);
    this.state$ = this.stateSubject.asObservable();
    this.changes$ = this.changesSubject.asObservable();
    this.saving$ = this.savingSubject.asObservable();
    this.saveError$ = this.saveErrorSubject.asObservable();
    this.dirty$ = this.changes$.pipe(map(c => c.length > 0));

    this.unregister = config.registry ? config.registry.register(this) : () => {};
  }

  /** Current (unsaved) state. */
  get state(): TState { return this.stateSubject.value; }

  /** The last successfully saved state — what Cancel returns to. */
  get savedState(): TState { return this.saved; }

  /** The unsaved operations, oldest first. */
  get ops(): readonly TOp[] { return this.stack; }

  get dirty(): boolean { return this.stack.length > 0; }

  get canUndo(): boolean { return this.stack.length > 0; }

  get saving(): boolean { return this.savingSubject.value; }

  /** Record one change. The only way state moves forward. */
  apply(op: TOp): void {
    this.stack = [...this.stack, op];
    this.publish();
  }

  /**
   * Record one change proposed by the Copilot.
   *
   * Exactly `apply`, with the origin stamped. It exists so that the marking is
   * done in ONE place: a Copilot proposal that reached the stack as a plain
   * `apply` would be indistinguishable from the user's own work, and the two
   * things the design asks for — purple in the other panels, and an undo that
   * removes it alone — both hang off the origin.
   *
   * The cast is the price of a generic operation type: `TOp` is the page's own
   * union and every member of it extends `EditSessionOp`, so it always has room
   * for `origin`, but TypeScript cannot see that through a spread of a generic.
   */
  propose(op: TOp): void {
    this.apply({ ...op, origin: 'copilot' } as TOp);
  }

  /** Record several changes as one step of history is NOT what this does — each
   *  op stays individually undoable, which is what the design asks for when the
   *  Copilot proposes a batch. Group at the reducer level if a batch must undo
   *  as a unit. */
  applyAll(ops: readonly TOp[]): void {
    if (!ops.length) return;
    this.stack = [...this.stack, ...ops];
    this.publish();
  }

  /** Step back one unsaved operation. */
  undo(): void {
    if (!this.stack.length) return;
    this.stack = this.stack.slice(0, -1);
    this.publish();
  }

  /** Drop one operation wherever it sits in the stack and re-fold the rest.
   *  This is how a single Copilot change is undone without discarding the user's
   *  later work. */
  undoAt(index: number): void {
    if (index < 0 || index >= this.stack.length) return;
    this.stack = this.stack.filter((_, i) => i !== index);
    this.publish();
  }

  /** Discard every unsaved operation: back to the last successful save. */
  cancel(): void {
    if (!this.stack.length) {
      // Still clear a stale failure banner — the user asked to start over.
      this.saveErrorSubject.next(null);
      return;
    }
    this.stack = [];
    this.saveErrorSubject.next(null);
    this.publish();
  }

  /**
   * Adopt a new saved baseline — a fresh server load, or an external change the
   * page decided to take. Clears the unsaved stack, so a page must not call this
   * on a background refresh while the user is editing.
   */
  reload(state: TState): void {
    this.saved = state;
    this.stack = [];
    this.saveErrorSubject.next(null);
    this.publish();
  }

  /**
   * Save everything, atomically, in one commit.
   *
   * On success the baseline moves to the saved state and the undo stack is
   * cleared. On failure NOTHING moves: the state the user is looking at, the
   * operation list, and the undo stack are exactly what they were, so Save can
   * simply be pressed again. The error is both pushed to `saveError$` and
   * rethrown, so a caller can toast it and a bar can render it.
   *
   * Edits made while a save is in flight survive it: the operations that were
   * committed are a prefix of the stack, so only that prefix is dropped and
   * anything typed since is re-folded on top of the new baseline.
   */
  save(commit: EditSessionCommit<TState, TOp> | undefined = this.commitFn): Observable<TState> {
    return defer(() => {
      if (!commit) {
        return throwError(() => new Error('EditSession.save() called with no commit function.'));
      }
      if (this.savingSubject.value) {
        return throwError(() => new Error('EditSession.save() is already in flight.'));
      }
      if (!this.dirty) return of(this.state);

      const committed = this.stack;
      const attempted = this.state;
      this.savingSubject.next(true);
      this.saveErrorSubject.next(null);

      return commit(attempted, committed).pipe(
        map(returned => (returned === undefined ? attempted : (returned as TState))),
        tap(next => {
          this.savingSubject.next(false);
          this.saved = next;
          // Drop exactly what was committed. `stack` may have grown since.
          this.stack = this.stack.slice(committed.length);
          this.publish();
        }),
        map(() => this.state),
        catchError(err => {
          // Deliberately touch nothing else: the edits are the user's to retry.
          this.savingSubject.next(false);
          this.saveErrorSubject.next(err);
          return throwError(() => err);
        }),
      );
    });
  }

  /** Release the registry entry. Call from the page's ngOnDestroy. */
  destroy(): void {
    this.unregister();
  }

  /** Re-fold and publish. One place, so state and the change strip can never
   *  describe different things. */
  private publish(): void {
    let next = this.saved;
    for (const op of this.stack) next = this.reduce(next, op);
    this.stateSubject.next(next);
    this.changesSubject.next(this.stack.map((op, index) => ({
      index,
      op,
      label: op.label,
      origin: op.origin ?? 'user',
    })));
  }
}
